import type { ParentWindowEventMessage } from '@inboundx/shared/analytics';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';

interface ReceivedEvent {
    id: number;
    timestamp: Date;
    data: ParentWindowEventMessage['payload'];
}

interface EventViewerPanelProps {
    /** Whether the panel is visible */
    isVisible: boolean;
}

/**
 * EventViewerPanel - Displays analytics events received via postMessage
 *
 * This panel listens for 'rose-analytics-event' messages and displays them
 * in a small floating panel at the top of the page. Useful for testing
 * the parent window event forwarding feature.
 */
export function EventViewerPanel({ isVisible }: EventViewerPanelProps) {
    const [events, setEvents] = useState<ReceivedEvent[]>([]);
    const [isMinimized, setIsMinimized] = useState(false);
    const eventCounterRef = useRef(0);

    // Listen for postMessage events
    useEffect(() => {
        if (!isVisible) return;

        const handleMessage = (event: MessageEvent) => {
            // Only handle rose-analytics-event messages
            if (event.data?.type !== 'rose-analytics-event') return;

            const payload = event.data.payload as ParentWindowEventMessage['payload'];

            eventCounterRef.current += 1;
            const newEvent: ReceivedEvent = {
                id: eventCounterRef.current,
                timestamp: new Date(),
                data: payload,
            };

            setEvents((prev) => {
                // Add new event and sort by timestamp (newest first)
                const updated = [newEvent, ...prev];
                return updated.slice(0, 50); // Keep last 50 events
            });
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [isVisible]);

    const clearEvents = useCallback(() => {
        setEvents([]);
    }, []);

    if (!isVisible) return null;

    return (
        <div
            className="fixed top-20 left-4 z-[1000] bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-700 text-white font-mono text-xs"
            style={{ maxWidth: '400px', minWidth: '300px' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
                <div className="flex items-center gap-2">
                    <span className="text-green-400">📡</span>
                    <span className="font-semibold text-sm">Event Viewer</span>
                    <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[10px]">
                        {events.length}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={clearEvents}
                        className="h-6 w-6 text-gray-400 hover:text-white hover:bg-gray-700"
                        title="Clear events"
                    >
                        <Trash2 className="h-3 w-3" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsMinimized(!isMinimized)}
                        className="h-6 w-6 text-gray-400 hover:text-white hover:bg-gray-700"
                        title={isMinimized ? 'Expand' : 'Minimize'}
                    >
                        {isMinimized ? (
                            <ChevronDown className="h-3 w-3" />
                        ) : (
                            <ChevronUp className="h-3 w-3" />
                        )}
                    </Button>
                </div>
            </div>

            {/* Events list */}
            {!isMinimized && (
                <div className="max-h-[300px] overflow-y-auto">
                    {events.length === 0 ? (
                        <div className="px-3 py-4 text-gray-400 text-center">
                            <p>No events received yet</p>
                            <p className="text-[10px] mt-1">
                                Interact with the widget to see events
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-700/50">
                            {events.map((event) => (
                                <EventItem key={event.id} event={event} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function EventItem({ event }: { event: ReceivedEvent }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'user_action':
                return 'bg-blue-600';
            case 'conversion':
                return 'bg-green-600';
            case 'system':
                return 'bg-gray-600';
            case 'navigation':
                return 'bg-purple-600';
            default:
                return 'bg-gray-600';
        }
    };

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    return (
        // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: expandable row in dev tool panel; keyboard nav not required
        <div
            className="px-3 py-2 hover:bg-gray-800/50 cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
        >
            <div className="flex items-center gap-2">
                <span className="text-gray-500 text-[10px]">{formatTime(event.timestamp)}</span>
                <span
                    className={`${getCategoryColor(event.data.category)} px-1.5 py-0.5 rounded text-[10px]`}
                >
                    {event.data.category}
                </span>
                <span className="text-yellow-400 font-medium truncate flex-1">
                    {event.data.eventType}
                </span>
                {isExpanded ? (
                    <ChevronUp className="h-3 w-3 text-gray-500" />
                ) : (
                    <ChevronDown className="h-3 w-3 text-gray-500" />
                )}
            </div>

            {isExpanded && (
                <div className="mt-2 pl-2 border-l-2 border-gray-700">
                    <div className="text-[10px] text-gray-400 mb-1">Properties:</div>
                    <pre className="text-[10px] text-gray-300 whitespace-pre-wrap break-all bg-gray-800/50 rounded p-2">
                        {JSON.stringify(event.data.properties, null, 2)}
                    </pre>
                    <div className="text-[10px] text-gray-400 mt-2 mb-1">Context:</div>
                    <pre className="text-[10px] text-gray-300 whitespace-pre-wrap break-all bg-gray-800/50 rounded p-2">
                        {JSON.stringify(event.data.context, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}
